import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Label,
	SettingsSection,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

export function JobLevelEditor(props: { jobLevel?: graphql.JobLevel }) {
	const { t } = useTranslation();
	const [error, attempt] = useErrorBoundary();
	const { addToast } = useToasts();
	const history = useHistory();
	const { isHRBusinessPartner } = useViewerRoles();

	const [create, { loading: createLoading }] = graphql.useCreateJobLevelMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateJobLevelMutation();

	const formik = useFormik({
		initialValues: {
			title: props.jobLevel?.title ?? [],
			description: props.jobLevel?.description ?? [],
		},
		onSubmit: attempt(async () => {
			if (props.jobLevel) {
				await update({
					variables: {
						input: {
							id: props.jobLevel.id,
							title: formik.values.title,
							description:
								formik.values.description.length > 0 ? formik.values.description : undefined,
						},
					},
				});
			} else {
				const response = await create({
					variables: {
						input: {
							title: formik.values.title,
							description:
								formik.values.description.length > 0 ? formik.values.description : undefined,
						},
					},
				});

				history.push(`/settings/jobs/levels/${response.data?.createJobLevel.jobLevel.id}`);
			}

			addToast(t('common.settings.jobLevels.edit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		}),
	});

	const loading = createLoading || updateLoading;

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			{!isHRBusinessPartner && (
				<Heading mb={0}>
					{t(`common.settings.jobLevels.${props.jobLevel ? 'edit' : 'create'}.title`)}
				</Heading>
			)}
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				{isHRBusinessPartner && (
					<Box>
						<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
							<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
								<Label>{t('common.settings.jobLevels.edit.name')}</Label>
							</Box>

							<Box width={[12 / 12, 12 / 12, 9 / 12]}>{props.jobLevel?.title?.[0]?.text}</Box>
						</Flex>
						<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
							<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
								<Label>{t('common.settings.jobLevels.edit.description')}</Label>
							</Box>

							<Box width={[12 / 12, 12 / 12, 9 / 12]}>
								<Box width={[12 / 12, 12 / 12, 9 / 12]}>
									{props.jobLevel?.description?.[0]?.text}
								</Box>
							</Box>
						</Flex>
					</Box>
				)}
				{!isHRBusinessPartner && (
					<SettingsSection first title={t('common.settings.jobLevels.edit.details')}>
						<Form>
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.jobLevels.edit.name'),
										name: 'title',
										type: DetailsTableFormValueTypes.Multilingual,
									},
									{
										label: t('common.settings.jobLevels.edit.description'),
										name: 'description',
										type: DetailsTableFormValueTypes.Multilingual,
									},
								]}
							/>
						</Form>
					</SettingsSection>
				)}
			</FormikProvider>

			{!isHRBusinessPartner && (
				<Flex justifyContent="flex-end">
					<Button
						onClick={() => formik.submitForm()}
						loading={loading}
						disabled={!formik.values.title.length}
						title={t('common.save')}
					/>
				</Flex>
			)}
			{props.jobLevel && !isHRBusinessPartner && (
				<SettingsSection title={t('common.settings.jobLevels.actions.title')}>
					<ActionArchiveListItem
						archivableItem={props.jobLevel}
						archivableItemName={t('common.settings.jobLevels.archivableItemName')}
						archivableItemKey="jobLevel"
						useArchive={graphql.useArchiveJobLevelMutation}
						useRestore={graphql.useRestoreJobLevelMutation}
					/>
				</SettingsSection>
			)}
		</FlexGap>
	);
}

export default function JobLevelEditorWithID(props: { id: string }) {
	const { data } = graphql.useJobLevelQuery({
		variables: {
			id: props.id,
		},
		fetchPolicy: 'network-only',
	});

	if (!data) {
		return <Spinner centered />;
	}

	return <JobLevelEditor jobLevel={data.jobLevel as graphql.JobLevel} />;
}
